import { useMemo, useState, useRef, useEffect } from "react";
import { Seo, breadcrumbSchema } from "@/lib/seo";
import { motion } from "framer-motion";
import api from "@/lib/api";
import MarketplaceProductCard from "@/components/MarketplaceProductCard";
import { CarePlans } from "@/components/CarePlans";
import { getTemplates } from "@/templates/registry";
import { COLLECTIONS, CAT_TO_COLLECTION, MORE_COLLECTION } from "@/lib/collections";
import { Search, LayoutGrid, X, Loader2, LayoutTemplate, ChevronDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;


// Human labels for product types (category-agnostic — ready for non-website products).
const PRODUCT_TYPE_LABELS = {
  website_template: "Website Design", business_plan: "Business Plan", ai_agent: "AI Agent",
  course: "Course", toolkit: "Toolkit", consulting: "Consulting", funding_package: "Funding Package",
  digital_product: "Digital Product",
};
export const productTypeLabel = (pt) => PRODUCT_TYPE_LABELS[pt] || "Website Design";

// Product-type filter is DESIGNED but OFF until there are meaningful non-website
// product categories. Flip to true (no refactor needed) once they exist.
const SHOW_PRODUCT_TYPE_FILTER = false;

// Slugs that have an in-app React design prototype (interactive concept demos).
const PROTOTYPE_SLUGS = new Set(getTemplates().map((t) => t.slug));

// Normalize a DB template record → the shape MarketplaceProductCard expects.
// Template Factory (the database) is the single source of truth; IDs are
// resolved per-environment so preview/demo/checkout always work.
function normalize(t) {
  const slug = t.slug || t.id;
  // A template is a "concept prototype" ONLY if it matches an in-app registry
  // demo AND is not a real deliverable. Real Product-Factory products (which
  // have a served live demo) must NEVER be shown as a concept — that would
  // misrepresent a purchasable product as a mockup.
  const showcase = PROTOTYPE_SLUGS.has(slug) && !t.live_demo;
  const tags = Array.isArray(t.tags) && t.tags.length
    ? t.tags
    : (t.badge ? String(t.badge).split(",").map((s) => s.trim()).filter(Boolean) : []);
  const resp = !!t.responsive;
  const outcomes = Array.isArray(t.outcomes) ? t.outcomes.filter(Boolean) : [];
  return {
    id: t.id,
    slug,
    name: t.title || "Design",
    category: t.category || "Business",
    description: t.description || "",
    // --- Solution Profile (solution-first browsing; graceful when unauthored) ---
    productType: t.product_type || "website_template",
    mission: t.mission || "",
    problemSolved: t.problem_solved || "",
    idealCustomer: t.target_customer || t.ideal_customer || "",
    outcomes,
    hasSolution: !!(t.solution_published_at || t.mission || t.problem_solved || outcomes.length),
    previewImage: t.preview_image || "",
    tags,
    price: t.template_price ?? 0,
    regularPrice: t.regular_price ?? t.template_price ?? 0,
    salePrice: (t.sale_price && t.sale_price > 0) ? t.sale_price : null,
    customPrice: t.custom_price ?? 0,
    series: t.series || "",
    badges: Array.isArray(t.badges) ? t.badges : [],
    featured: !!t.featured,
    isNew: !!t.is_new,
    responsive: { desktop: resp, tablet: resp, mobile: resp },
    downloadable: !!t.downloadable,
    // A demo exists for a Product-Factory product with a served demo, or any
    // design prototype (rendered in-app). Otherwise the Live Demo button hides.
    hasDemo: showcase || !!t.live_demo,
    showcase,
    previewTo: `/template/${slug}`,
    demoTo: `/marketplace/demo/${slug}`,
  };
}

// Lightweight relevance filter over name/category/tags/description + solution copy.
function matches(t, q) {
  if (!q) return true;
  const hay = `${t.name} ${t.category} ${t.description} ${(t.tags || []).join(" ")} ${t.problemSolved} ${t.idealCustomer} ${(t.outcomes || []).join(" ")}`.toLowerCase();
  return hay.includes(q);
}

const SS = {
  q: "mp_q", cat: "mp_cat", scroll: "mp_scroll", ptype: "mp_ptype",
};

export default function Marketplace() {
  const [category, setCategory] = useState(() => sessionStorage.getItem(SS.cat) || "All");
  const [ptype, setPtype] = useState(() => sessionStorage.getItem(SS.ptype) || "All");
  const [q, setQ] = useState(() => sessionStorage.getItem(SS.q) || "");
  const [items, setItems] = useState(null); // null = loading
  const filtersRef = useRef(null);
  const gridRef = useRef(null);

  // Load real templates from the database (active + non-archived only).
  useEffect(() => {
    api.get("/templates").then((r) => {
      // Showroom guard: never surface a design that is missing customer-facing
      // essentials (description + preview image). Keeps incomplete/placeholder
      // records out of the client-facing showroom automatically.
      const norm = (r.data || [])
        .map(normalize)
        .filter((t) => t.name && t.description && t.description.trim() && t.previewImage);
      setItems(norm);
    }).catch(() => setItems([]));
  }, []);

  // Persist search + category so they survive returning from a product page.
  useEffect(() => { sessionStorage.setItem(SS.q, q); }, [q]);
  useEffect(() => { sessionStorage.setItem(SS.cat, category); }, [category]);
  useEffect(() => { sessionStorage.setItem(SS.ptype, ptype); }, [ptype]);

  // Save scroll position continuously; restore after data renders.
  useEffect(() => {
    const onScroll = () => sessionStorage.setItem(SS.scroll, String(window.scrollY));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    if (items === null) return;
    // Deep-link from Product Detail care-plan nudges → scroll to Hosting & Care.
    if (window.location.hash === "#care-plans") {
      requestAnimationFrame(() => {
        setTimeout(() => document.getElementById("care-plans")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      });
      return;
    }
    const y = parseInt(sessionStorage.getItem(SS.scroll) || "0", 10);
    if (y > 0) requestAnimationFrame(() => window.scrollTo({ top: y }));
  }, [items]);

  // Browse by curated Collection (not raw DB categories) — fewer, clearer,
  // shopper-oriented chips that match how clients actually shop.
  const collectionChips = useMemo(() => {
    const chips = [{ key: "All", label: "All" }];
    if (!items) return chips;
    const present = new Set(items.filter((t) => !t.showcase).map((t) => CAT_TO_COLLECTION[t.category] || "more"));
    COLLECTIONS.forEach((c) => { if (present.has(c.key)) chips.push({ key: c.key, label: c.label }); });
    if (present.has("more")) chips.push({ key: "more", label: "More Designs" });
    return chips;
  }, [items]);
  const activeLabel = useMemo(() => collectionChips.find((c) => c.key === category)?.label || "All", [collectionChips, category]);

  // Product types present in the catalog (drives the future product-type filter).
  const productTypes = useMemo(() => {
    if (!items) return ["All"];
    const set = new Set(items.map((t) => t.productType).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const query = q.trim().toLowerCase();
    return items.filter((t) =>
      (category === "All" || (CAT_TO_COLLECTION[t.category] || "more") === category)
      && (ptype === "All" || t.productType === ptype)
      && matches(t, query));
  }, [items, category, ptype, q]);

  const products = useMemo(() => filtered.filter((t) => !t.showcase), [filtered]);
  const showcase = useMemo(() => filtered.filter((t) => t.showcase), [filtered]);

  // Collections view is active only when browsing everything (no filter/search).
  const collectionsView = category === "All" && ptype === "All" && !q.trim();
  const [collapsed, setCollapsed] = useState({});
  const toggleCollection = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  // Bucket products into curated collections (order preserved), with a fallback.
  const grouped = useMemo(() => {
    const buckets = {};
    for (const p of products) {
      const key = CAT_TO_COLLECTION[p.category] || "more";
      (buckets[key] = buckets[key] || []).push(p);
    }
    const ordered = COLLECTIONS
      .filter((c) => (buckets[c.key] || []).length)
      .map((c) => ({ ...c, products: buckets[c.key] }));
    if ((buckets.more || []).length) {
      ordered.push({ ...MORE_COLLECTION, products: buckets.more });
    }
    return ordered;
  }, [products]);

  const scrollToResults = () => {
    requestAnimationFrame(() => {
      const el = filtersRef.current;
      if (!el) return;
      const nav = document.querySelector('[data-testid="navbar"]');
      const navH = nav ? nav.getBoundingClientRect().height : 80;
      const y = el.getBoundingClientRect().top + window.scrollY - navH - 100;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    });
  };
  const onSearchSubmit = (e) => { e.preventDefault(); scrollToResults(); };

  return (
    <div>
      <Seo
        title="Our Work — Website Designs We've Built | ATF WebWorks"
        description="See real website designs ATF WebWorks has built. Choose one and we'll customize, launch, host, and grow it for your business — custom builds starting at $997."
        path="/marketplace"
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Our Work", path: "/marketplace" }])]}
      />
      {/* HERO HEADER */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
              <span className="h-px w-8 bg-gold" /> Our Work
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-3xl">
              Designs we've built. <span className="text-gold">Yours next.</span>
            </h1>
            <p className="text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              Every design below is a real, fully functional site we've built — walk through the live demo, scroll, click and resize across devices. Choose the one you love and we'll tailor it to your business, launch it, host it, and help you grow — with AI-powered planning and ongoing support built in.
            </p>

            {/* Single search field */}
            <form onSubmit={onSearchSubmit} className="mt-10 max-w-xl relative" data-testid="marketplace-search-form">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={19} />
              <input
                value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search designs, categories, tags…"
                data-testid="marketplace-search" enterKeyHint="search"
                className="w-full bg-navy-800 border border-white/15 text-white pl-12 pr-11 py-4 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold transition-colors"
              />
              {q && (
                <button type="button" onClick={() => setQ("")} data-testid="marketplace-search-clear" aria-label="Clear search"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors">
                  <X size={18} />
                </button>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <div id="marketplace-results" className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        {/* Pinned category filters (no duplicate search bar). */}
        <div className="sticky top-20 z-40 -mx-6 md:-mx-12 px-6 md:px-12 py-5 bg-navy/90 backdrop-blur-xl border-b border-white/10">
          <div ref={filtersRef} className="flex flex-wrap gap-2.5 scroll-mt-28" data-testid="category-filters">
            {collectionChips.map((c, i) => (
              <motion.button
                key={c.key} onClick={() => setCategory(c.key)}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                data-testid={`category-${c.key}`}
                className={`px-4 py-2 text-sm border transition-all duration-200 ${category === c.key ? "bg-gold text-navy border-gold font-semibold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}
              >
                {c.label}
              </motion.button>
            ))}
          </div>
          {/* Product-type filter — ready but hidden until non-website products exist. */}
          {SHOW_PRODUCT_TYPE_FILTER && productTypes.length > 2 && (
            <div className="flex flex-wrap gap-2.5 mt-3" data-testid="producttype-filters">
              {productTypes.map((p) => (
                <button key={p} onClick={() => setPtype(p)} data-testid={`producttype-${p}`}
                  className={`px-3.5 py-1.5 text-xs border transition-all duration-200 ${ptype === p ? "bg-white text-navy border-white font-semibold" : "border-white/15 text-gray-400 hover:border-gold/50 hover:text-white"}`}>
                  {p === "All" ? "All Types" : productTypeLabel(p)}
                </button>
              ))}
            </div>
          )}
        </div>

        {items === null ? (
          <div className="py-32 grid place-items-center text-gray-400" data-testid="marketplace-loading">
            <Loader2 className="animate-spin text-gold" size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-32 border border-dashed border-white/10 mt-10" data-testid="no-results">
            <LayoutGrid className="mx-auto mb-4 text-gray-600" size={32} />
            <p className="text-gray-400 max-w-md mx-auto">No designs matched your search. Try different keywords or browse all collections.</p>
            {(q || category !== "All" || ptype !== "All") && (
              <button onClick={() => { setQ(""); setCategory("All"); setPtype("All"); }} data-testid="clear-search"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 text-sm font-medium border border-gold/50 text-gold hover:bg-gold/10 transition-colors">
                Browse all designs
              </button>
            )}
          </div>
        ) : (
          <div ref={gridRef}>
            {/* PURCHASABLE PRODUCTS */}
            {products.length > 0 && collectionsView && (
              <section className="pt-6" data-testid="section-collections">
                <div className="flex items-center justify-between text-sm text-gray-400 mb-8 gap-4 flex-wrap">
                  <div>
                    <h2 className="font-heading text-2xl text-white mb-1">Browse by Collection</h2>
                    <span className="flex items-center gap-2" data-testid="products-count"><LayoutGrid size={15} className="text-gold" /> {products.length} {products.length === 1 ? "design" : "designs"} across {grouped.length} collections</span>
                  </div>
                </div>
                <div className="space-y-14">
                  {grouped.map((col) => {
                    const isCollapsed = !!collapsed[col.key];
                    const Icon = col.Icon;
                    return (
                      <div key={col.key} data-testid={`collection-${col.key}`}>
                        <button
                          onClick={() => toggleCollection(col.key)}
                          data-testid={`collection-toggle-${col.key}`}
                          className="w-full flex items-center gap-4 text-left group mb-6 border-b border-white/10 pb-4"
                        >
                          <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-gold/10 text-gold border border-gold/20 shrink-0">
                            <Icon size={20} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="font-heading text-xl text-white block group-hover:text-gold transition-colors duration-200">{col.label}</span>
                            <span className="text-sm text-gray-400">{col.blurb} · {col.products.length} {col.products.length === 1 ? "design" : "designs"}</span>
                          </span>
                          <ChevronDown size={20} className={`text-gray-400 shrink-0 transition-transform duration-300 ${isCollapsed ? "-rotate-90" : ""}`} />
                        </button>
                        {!isCollapsed && (
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {col.products.map((t, i) => <MarketplaceProductCard key={t.slug} template={t} index={i} />)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* FILTERED / SEARCH RESULTS (flat grid) */}
            {products.length > 0 && !collectionsView && (
              <section className="pt-6" data-testid="section-products">
                <div className="flex items-center justify-between text-sm text-gray-400 mb-8 gap-4 flex-wrap">
                  <div>
                    <h2 className="font-heading text-2xl text-white mb-1">Website Designs</h2>
                    <span className="flex items-center gap-2" data-testid="products-count"><LayoutGrid size={15} className="text-gold" /> {products.length} {products.length === 1 ? "design" : "designs"}{category !== "All" ? ` in ${activeLabel}` : ""}</span>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="templates-grid">
                  {products.map((t, i) => <MarketplaceProductCard key={t.slug} template={t} index={i} />)}
                </div>
              </section>
            )}

            {/* DESIGN SHOWCASE (concept prototypes) */}
            {showcase.length > 0 && (
              <section className={products.length > 0 ? "mt-20 pt-14 border-t border-white/10" : "pt-6"} data-testid="section-showcase">
                <div className="mb-8">
                  <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-3">
                    <LayoutTemplate size={14} /> Design Showcase
                  </div>
                  <h2 className="font-heading text-2xl text-white mb-1">Concept designs & inspiration</h2>
                  <span className="flex items-center gap-2 text-sm text-gray-400" data-testid="showcase-count"><LayoutGrid size={15} className="text-gold" /> {showcase.length} interactive {showcase.length === 1 ? "concept" : "concepts"} — request a custom build to make one yours</span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="showcase-grid">
                  {showcase.map((t, i) => <MarketplaceProductCard key={t.slug} template={t} index={i} />)}
                </div>
              </section>
            )}

            {/* HOSTING & CARE — recurring plans (shown in the default browse view) */}
            {collectionsView && (
              <section id="care-plans" className="mt-20 pt-14 border-t border-white/10" data-testid="section-care-plans">
                <CarePlans />
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
